'use client'

import { confirm } from '@repo/ui/Confirm'
import { DeleteIcon } from '@repo/ui/DeleteIcon'
import { DocumentIcon } from '@repo/ui/DocumentIcon'
import { EditIcon } from '@repo/ui/EditIcon'
import { useEffect, useRef, useState } from 'react'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useWorkflowStatusStore } from '@repo/store/useWorkflowStatusStore'
import MUIButton from '@mui/material/Button'
import MUIIcon from '@mui/material/Icon'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITextField from '@mui/material/TextField'
import MUITypography from '@mui/material/Typography'
import MUITooltip from '@mui/material/Tooltip'
import styles from './StatusBar.module.scss'

export const StatusBar = () => {
  const { currentMatter } = useMattersStore()
  const { fetchedWorkflowStatus, fetchWorkflowStatus, fetchedWorkflowStatusPermissions, updateWorkflowStatus } =
    useWorkflowStatusStore()
  const { clientMatterNo } = currentMatter || {}
  const [showSavedIcon, setShowSavedIcon] = useState<boolean>(false)
  const [notSaved, setNotSaved] = useState<boolean>(false)
  const [editable, setEditable] = useState<boolean>(false)
  const [defaultValue, setDefaultValue] = useState<string>('')
  const [originalValue, setOriginalValue] = useState<string>('')
  const textFieldRef = useRef<HTMLInputElement | null>(null)

  const handleEditClick = () => {
    setEditable((prev) => !prev)
    setNotSaved(false)
    setShowSavedIcon(true)
    !notSaved &&
      setTimeout(() => {
        textFieldRef.current?.focus()
      }, 0)
  }

  useEffect(() => {
    clientMatterNo && fetchWorkflowStatus({ clientMatterNo })
  }, [clientMatterNo])

  useEffect(() => {
    if (!fetchedWorkflowStatus) return

    const newValue = typeof fetchedWorkflowStatus === 'object' ? fetchedWorkflowStatus.data : fetchedWorkflowStatus

    setDefaultValue(newValue)
    setOriginalValue(newValue)
  }, [fetchedWorkflowStatus])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDefaultValue(event.target.value)
  }

  const handleBlur = () => {
    if (!editable || !clientMatterNo) return
    setNotSaved(true)
  }

  const handleSaveClick = () => {
    setShowSavedIcon(false)
    setNotSaved(true)
    setEditable(false)
    // Only update if defaultValue is not empty and has changed
    if (defaultValue === '' || defaultValue === originalValue) return

    notSaved &&
      updateWorkflowStatus({
        clientMatterNo,
        workflowStatus: defaultValue,
        successToastMessage: `Workflow status updated to: ${defaultValue}`
      })
  }

  const handleDeleteClick = async () => {
    if (clientMatterNo) {
      const proceed = await confirm('Are you sure you want to delete this Workflow Status?')
      if (!proceed) return

      setDefaultValue('')

      proceed &&
        updateWorkflowStatus({
          clientMatterNo,
          workflowStatus: '',
          successToastMessage: `Workflow status has been deleted`
        })
    }
  }

  return (
    <MUIPaper
      data-testid="StatusBar"
      sx={{
        background: 'white',
        border: '1px solid #D8DCE5',
        borderLeft: 'solid 8px #A59FD0',
        borderRadius: 1,
        boxShadow: 'none',
        height: 'auto',
        margin: '1.5rem auto 0',
        maxWidth: '1520px',
        padding: '0.5rem 12px',
        position: 'relative',
        width: 'calc(100% - 80px)'
      }}
    >
      <MUIStack direction="row" alignItems="center" gap={4}>
        <DocumentIcon sx={{ height: '20px' }} />
        <MUITypography variant="body1" sx={{ margin: 0, fontWeight: '700' }}>
          Workflow status:
        </MUITypography>
        <MUITextField
          className={styles.textField}
          variant="standard"
          size="small"
          inputRef={textFieldRef}
          placeholder={fetchedWorkflowStatusPermissions.canUpdate ? 'Enter workflow status' : 'No workflow status set'}
          value={defaultValue}
          onChange={handleChange}
          onBlur={handleBlur}
          focused={editable}
          sx={{
            flex: 1,
            margin: 0,
            '.MuiInputBase-input.Mui-disabled': {
              backgroundColor: '#FFF !important',
              color: 'rgba(5, 28, 51, 0.87)',
              WebkitTextFillColor: 'rgba(5, 28, 51, 0.87)'
            }
          }}
          disabled={fetchedWorkflowStatusPermissions.canUpdate === false ? true : !editable}
          inputProps={{
            'aria-readonly': editable,
            className: styles.inputBar,
            maxLength: 255,
            style: {
              borderBottom: 'none',
              paddingBottom: '4px',
              position: 'relative',
              top: '0',
              paddingTop: '4px'
            }
          }}
        />
        {fetchedWorkflowStatusPermissions.canUpdate === false ? null : (
          <>
            {!showSavedIcon ? (
              <>
                <MUITooltip title={`Edit workflow status`} placement="top" arrow>
                  <MUIIcon onClick={handleEditClick} className={styles.btn}>
                    <EditIcon sx={{ height: '20px', position: 'absolute', top: 0, left: 0, width: '22px' }} />
                  </MUIIcon>
                </MUITooltip>
                <div className={styles.divider} />
                <MUITooltip title={`Delete workflow status`} placement="top" arrow>
                  <MUIIcon onClick={handleDeleteClick} className={styles.btn}>
                    <DeleteIcon sx={{ height: '20px', position: 'absolute', top: 0, left: 0, width: '22px' }} />
                  </MUIIcon>
                </MUITooltip>
              </>
            ) : (
              <MUIButton onClick={handleSaveClick} sx={{ padding: 0 }}>
                <MUITypography
                  variant="body1"
                  sx={{ cursor: 'pointer', margin: '0', fontWeight: '700', height: '20px' }}
                >
                  Save
                </MUITypography>
              </MUIButton>
            )}
          </>
        )}
      </MUIStack>
    </MUIPaper>
  )
}
